// src/types/extraction.ts

import type { PartialDeal } from "./deal";
import type { Nda } from "./nda";

export type ExtractionStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed"
  | "cancelled";

export type ImageExtractionStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed"
  | "skipped";

export type ExtractionImageType =
  | "exterior_photo"
  | "aerial_photo"
  | "interior_photo"
  | "render"
  | "site_plan"
  | "location_map"
  | "floor_plan"
  | "table"
  | "chart"
  | "logo"
  | "headshot"
  | "text_graphic"
  | "unknown";

export const EXTRACTION_IMAGE_TYPE_LABELS: Record<ExtractionImageType, string> =
  {
    exterior_photo: "Exterior",
    aerial_photo: "Aerial",
    interior_photo: "Interior",
    render: "Render",
    site_plan: "Site plan",
    location_map: "Location map",
    floor_plan: "Floor plan",
    table: "Table",
    chart: "Chart",
    logo: "Logo",
    headshot: "Headshot",
    text_graphic: "Text graphic",
    unknown: "Unknown",
  };

export interface ExtractionImage {
  id: number;
  ai_extraction_id: number;
  deal_id: number | null;
  pdf_index: number;
  page_number: number;
  image_index: number;
  url: string;
  width: number;
  height: number;
  image_type: ExtractionImageType;
  confidence: number;
  contains_text: boolean;
  likely_marketing_image: boolean;
  quality_score: number;
  ai_score: number;
  is_hero: boolean;
  is_selected: boolean;
  included_in_gpt_pipeline: boolean;
  caption: string | null;
  created_at: string;
}

export interface ExtractionImagesResponse {
  extraction_id: number;
  image_extraction_status: ImageExtractionStatus;
  contact_sheet_url: string | null;
  hero: ExtractionImage | null;
  selected: ExtractionImage[];
  images: ExtractionImage[];
}

export interface Extraction {
  id: number;
  user_id: number | null;
  status: ExtractionStatus;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  file_name: string;
  file_size_bytes: number | null;
  file_extension: string | null;
  excel_provider: string;
  is_term_sheet: boolean;
  is_initial_materials?: boolean;
  metadata?: {
    is_term_sheet?: boolean;
    is_initial_materials?: boolean;
  };
  assets: string[] | null;
  used_for_deal?: boolean;
  deal_id?: number | null;
  deal_name?: string | null;
  deal_data?: object | null;
  asset_data?: any[] | null;
  /** Top-level NDA draft from AI; not a persisted row (no id). Prefer over legacy deal_data.nda. */
  nda_data?: Nda | null;
  email_metadata?: object | null;
  attachments_info?: any[] | null;
  processed_files?: any[] | null;
  debug_ci_summary?: string | null;
  error_message?: string | null;
  tenant_dataframe?: any[] | null;
  user_name?: string | null;
  extract_images?: boolean;
  image_extraction_status?: ImageExtractionStatus | null;
  image_extraction_error?: string | null;
  image_extraction_completed_at?: string | null;
  contact_sheet_blob_path?: string | null;
}

export interface ExtractionListResponse {
  extractions: Extraction[];
  count: number;
}

export interface ExtractionStatusResponse {
  id: number;
  status: ExtractionStatus;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  error_message: string | null;
  extract_images?: boolean;
  image_extraction_status?: ImageExtractionStatus | null;
  image_extraction_error?: string | null;
  image_extraction_completed_at?: string | null;
}

export interface ExtractionCreateResponse {
  id: number;
  status: ExtractionStatus;
  file_name: string;
  deal_id?: number | null;
  created_at: string;
  message?: string;
  /** True when the backend reused an existing extraction instead of creating a new job. */
  duplicate?: boolean;
}

export interface ExtractionFilters {
  status?: ExtractionStatus;
  limit?: number;
  offset?: number;
}

export interface TenantColumnRange {
  cell_range: string;
  field_name: string;
  sheet_name: string;
  header_cell: string;
}

export interface ExtractionResult {
  id: number;
  fileName?: string;
  fileExtension?: string | null;
  excelProvider?: string;
  deal: PartialDeal;
  assets: any[];
  tenantData: any[];
  tenantColumnRanges: TenantColumnRange[];
  attachmentIndex?: number;
  assetNames?: string[];
  matchedAssets?: Record<string, unknown> | null;
  dealId?: number | null;
  usedForDeal?: boolean;
  isTermSheet?: boolean;
  isInitialMaterials?: boolean;
  /** Draft NDA from extraction (`nda_data`); create via POST /ndas after the deal exists. */
  nda?: Nda | null;
}
